import { useCallback, useEffect, useRef, useState } from 'react';

const makeId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const safeCreatePreview = (file) => {
  try {
    return URL.createObjectURL(file);
  } catch {
    return '';
  }
};

const safeRevokePreview = (url) => {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
};

/**
 * @typedef {Object} ImageUploadEntry
 * @property {string} id
 * @property {File} file
 * @property {string} preview
 */

/**
 * Manages image uploads + previews for a composer.
 * - maxImages limit
 * - creates + revokes object URLs
 * - supports optimistic "clear on send" with rollback
 */
export function useImageUploads({ maxImages = 6 } = {}) {
  /** @type {[ImageUploadEntry[], Function]} */
  const [images, _setImages] = useState([]);
  const imagesRef = useRef(images);

  // Batches removed from UI but not finalized yet (e.g., while sending).
  // Each element is an array of ImageUploadEntry.
  const pendingRef = useRef([]);

  const setImages = useCallback((next) => {
    imagesRef.current = next;
    _setImages(next);
  }, []);

  const updateImages = useCallback((updater) => {
    const prev = imagesRef.current;
    const next = updater(prev);
    imagesRef.current = next;
    _setImages(next);
    return next;
  }, []);

  const revokeEntries = useCallback((entries) => {
    if (!entries?.length) return;
    for (const entry of entries) {
      safeRevokePreview(entry?.preview);
    }
  }, []);

  const addFiles = useCallback(
    (filesLike) => {
      const files = Array.from(filesLike || []);
      if (files.length === 0) return;

      updateImages((prev) => {
        const remaining = maxImages - prev.length;
        if (remaining <= 0) return prev;

        const nextEntries = files.slice(0, remaining).map((file) => ({
          id: makeId(),
          file,
          preview: safeCreatePreview(file),
        }));

        return [...prev, ...nextEntries];
      });
    },
    [maxImages, updateImages]
  );

  const removeImage = useCallback(
    (id) => {
      updateImages((prev) => {
        const idx = prev.findIndex((entry) => entry.id === id);
        if (idx === -1) return prev;

        const removed = prev[idx];
        safeRevokePreview(removed?.preview);

        const next = prev.slice();
        next.splice(idx, 1);
        return next;
      });
    },
    [updateImages]
  );

  const clearImages = useCallback(() => {
    updateImages((prev) => {
      revokeEntries(prev);
      return [];
    });
  }, [revokeEntries, updateImages]);

  /**
   * Optimistically clears images from the UI, but keeps previews alive
   * until you either commit (success) or rollback (failure).
   *
   * @returns {ImageUploadEntry[]} batch
   */
  const prepareForSend = useCallback(() => {
    const batch = imagesRef.current;
    if (!batch.length) return [];

    pendingRef.current.push(batch);
    setImages([]);
    return batch;
  }, [setImages]);

  /**
   * Call after successful send: revokes previews for that batch.
   */
  const commitSend = useCallback(
    (batch) => {
      if (!batch?.length) return;
      pendingRef.current = pendingRef.current.filter((b) => b !== batch);
      revokeEntries(batch);
    },
    [revokeEntries]
  );

  /**
   * Call after failed send: restores that batch to the UI.
   */
  const rollbackSend = useCallback(
    (batch) => {
      if (!batch?.length) return;
      pendingRef.current = pendingRef.current.filter((b) => b !== batch);
      setImages(batch);
    },
    [setImages]
  );

  // Unmount cleanup: revoke any previews still alive (including pending batches)
  useEffect(() => {
    return () => {
      revokeEntries(imagesRef.current);
      for (const batch of pendingRef.current) {
        revokeEntries(batch);
      }
      pendingRef.current = [];
    };
  }, [revokeEntries]);

  const remainingSlots = Math.max(0, maxImages - images.length);
  const canAddMore = remainingSlots > 0;

  return {
    images,
    addFiles,
    removeImage,
    clearImages,
    prepareForSend,
    commitSend,
    rollbackSend,
    remainingSlots,
    canAddMore,
    maxImages,
  };
}
